import { FormEvent, useEffect, useState } from 'react';
import { api, money, PriceList, Product, VAT_OPTIONS } from '../api';
import { useSession } from '../session';
import { ErrorBox, Field, useAsync } from '../components/ui';

const num = (s: string) => Number(String(s).replace(/\s/g, '').replace(',', '.'));

/** Termékkatalógus és árlisták (M3). Szerkeszteni a tulajdonos és az admin tud. */
export function ProductsPage() {
  const s = useSession();
  const canEdit = s.role === 'owner' || s.role === 'admin';
  const [tab, setTab] = useState<'products' | 'lists'>('products');
  return (
    <div>
      <div className="page-head">
        <h2>Termékek és árlisták</h2>
        <div className="tabs inline">
          <button className={tab === 'products' ? '' : 'secondary'} onClick={() => setTab('products')}>Termékek</button>
          <button className={tab === 'lists' ? '' : 'secondary'} onClick={() => setTab('lists')}>Árlisták</button>
        </div>
      </div>
      {tab === 'products' ? <ProductList canEdit={canEdit} /> : <PriceLists canEdit={canEdit} />}
    </div>
  );
}

function ProductList({ canEdit }: { canEdit: boolean }) {
  const [q, setQ] = useState('');
  const [inactive, setInactive] = useState(false);
  const [data, setData] = useState<{ items: Product[]; total: number } | null>(null);
  const [edit, setEdit] = useState<Product | 'new' | null>(null);
  const a = useAsync();
  const load = () => a.run(async () => setData(await api(`/products?query=${encodeURIComponent(q)}&limit=200${inactive ? '&include_inactive=true' : ''}`)));
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [q, inactive]);
  return (
    <>
      <div className="filters">
        <input className="search" placeholder="Keresés cikkszám vagy név szerint…" value={q} onChange={(e) => setQ(e.target.value)} style={{ maxWidth: 360 }} />
        <label className="check"><input type="checkbox" checked={inactive} onChange={(e) => setInactive(e.target.checked)} /> inaktívak is</label>
        <span className="spacer" />
        {canEdit && <button onClick={() => setEdit('new')}>Új termék</button>}
      </div>
      {edit && <section className="card"><h3>{edit === 'new' ? 'Új termék' : edit.name}</h3>
        <ProductForm initial={edit === 'new' ? undefined : edit} onDone={() => { setEdit(null); load(); }} onCancel={() => setEdit(null)} /></section>}
      <ErrorBox error={a.error} />
      <table className="list">
        <thead><tr><th>Cikkszám</th><th>Megnevezés</th><th>Egység</th><th className="num">Nettó listaár</th><th>ÁFA</th></tr></thead>
        <tbody>
          {data?.items.map((p) => (
            <tr key={p.id} onClick={() => canEdit && setEdit(p)} className={p.active ? '' : 'muted'}>
              <td>{p.sku}</td><td>{p.name}{!p.active && <span className="badge">inaktív</span>}{p.description && <div className="muted small">{p.description.slice(0, 120)}</div>}</td>
              <td>{p.unit}</td><td className="num">{money(p.unit_price, p.currency)}</td><td>{p.vat_code}{/^\d+$/.test(p.vat_code) ? '%' : ''}</td>
            </tr>
          ))}
          {data?.items.length === 0 && <tr><td colSpan={5} className="muted">Nincs termék. {canEdit ? 'Vegyél fel egyet, vagy importáld a katalógust.' : ''}</td></tr>}
        </tbody>
      </table>
    </>
  );
}

function ProductForm({ initial, onDone, onCancel }: { initial?: Product; onDone: () => void; onCancel: () => void }) {
  const [v, setV] = useState({
    sku: initial?.sku ?? '', name: initial?.name ?? '', description: initial?.description ?? '', unit: initial?.unit ?? 'db',
    unit_price: initial ? String(initial.unit_price) : '', currency: initial?.currency ?? 'HUF', vat_code: initial?.vat_code ?? '27', active: initial?.active ?? true,
  });
  const a = useAsync();
  const set = (k: keyof typeof v) => (e: { target: { value: string } }) => setV({ ...v, [k]: e.target.value });
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const body = { ...v, description: v.description.trim() || null, unit_price: num(v.unit_price) };
    if (await a.run(() => api(initial ? `/products/${initial.id}` : '/products', { method: initial ? 'PATCH' : 'POST', body }))) onDone();
  };
  const archive = async () => { if (window.confirm('Törlöd a terméket a katalógusból? A korábbi ajánlatokban megmarad.') && await a.run(() => api(`/products/${initial!.id}/archive`, { method: 'POST' }))) onDone(); };
  return (
    <form className="form" onSubmit={submit}>
      <div className="grid2">
        <Field label="Cikkszám *"><input value={v.sku} onChange={set('sku')} required maxLength={50} /></Field>
        <Field label="Megnevezés *"><input value={v.name} onChange={set('name')} required maxLength={300} /></Field>
        <Field label="Nettó listaár *"><div className="row"><input inputMode="decimal" value={v.unit_price} onChange={set('unit_price')} required />
          <select value={v.currency} onChange={set('currency')} style={{ width: 90 }} aria-label="Pénznem"><option>HUF</option><option>EUR</option></select></div></Field>
        <Field label="Mennyiségi egység"><input value={v.unit} onChange={set('unit')} maxLength={20} list="units" /></Field>
        <Field label="ÁFA"><select value={v.vat_code} onChange={set('vat_code')}>{VAT_OPTIONS.map(([c, l]) => <option key={c} value={c}>{l}</option>)}</select></Field>
        <Field label="Állapot"><label className="check"><input type="checkbox" checked={v.active} onChange={(e) => setV({ ...v, active: e.target.checked })} /> aktív (ajánlatba tehető)</label></Field>
      </div>
      <datalist id="units">{['db', 'óra', 'nap', 'hó', 'év', 'kg', 'm', 'm²', 'm³', 'liter', 'csomag', 'alkalom', 'licenc'].map((u) => <option key={u} value={u} />)}</datalist>
      <Field label="Leírás" hint="az ajánlatban a megnevezés alatt jelenik meg"><textarea rows={2} value={v.description} onChange={set('description')} maxLength={5000} /></Field>
      <ErrorBox error={a.error} />
      <div className="actions">
        <button type="submit" disabled={a.busy}>Mentés</button>
        <button type="button" className="secondary" onClick={onCancel}>Mégse</button>
        {initial && <button type="button" className="danger" onClick={archive}>Törlés</button>}
      </div>
    </form>
  );
}

function PriceLists({ canEdit }: { canEdit: boolean }) {
  const [lists, setLists] = useState<PriceList[]>([]);
  const [cur, setCur] = useState<PriceList | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [add, setAdd] = useState('');
  const [saved, setSaved] = useState(false);
  const a = useAsync();
  const loadLists = (pick?: string) => a.run(async () => {
    const r = (await api<{ items: PriceList[] }>('/pricelists')).items;
    setLists(r);
    const id = pick ?? r[0]?.id;
    setCur(id ? await api<PriceList>(`/pricelists/${id}`) : null);
  });
  useEffect(() => { loadLists(); api<{ items: Product[] }>('/products?limit=200').then((r) => setProducts(r.items)).catch(() => undefined); }, []);
  const save = async () => {
    if (!cur) return;
    const body = { name: cur.name, currency: cur.currency, valid_from: cur.valid_from || null, valid_to: cur.valid_to || null,
      items: (cur.items ?? []).map((i) => ({ product_id: i.product_id, unit_price: Number(i.unit_price) })) };
    const r = await a.run(() => api<PriceList>(cur.id ? `/pricelists/${cur.id}` : '/pricelists', { method: cur.id ? 'PUT' : 'POST', body }));
    if (r) { setSaved(true); loadLists(r.id); }
  };
  const upd = (patch: Partial<PriceList>) => { setSaved(false); setCur({ ...cur!, ...patch }); };
  return (
    <div className="cols">
      <section className="card">
        <div className="card-head"><h3>Árlisták</h3>{canEdit && <button className="secondary" onClick={() => { setSaved(false); setCur({ id: '', name: 'Új árlista', currency: 'HUF', valid_from: null, valid_to: null, items: [] }); }}>Új árlista</button>}</div>
        <ul className="plain">
          {lists.map((l) => <li key={l.id}><button className="link inline" onClick={() => loadLists(l.id)}>{l.name}</button> <span className="muted small">{l.currency} · {l.item_count} tétel</span></li>)}
          {lists.length === 0 && <li className="muted">Nincs árlista. Árlista nélkül a katalógusár érvényes.</li>}
        </ul>
        <p className="muted small">Az ajánlatnál választható árlista; amelyik termék nincs rajta, annál a katalógusár a listaár. A kedvezményt az ajánlat ehhez méri.</p>
      </section>
      {cur && (
        <section className="card">
          <div className="grid2 form">
            <Field label="Név"><input value={cur.name} onChange={(e) => upd({ name: e.target.value })} disabled={!canEdit} /></Field>
            <Field label="Pénznem"><select value={cur.currency} onChange={(e) => upd({ currency: e.target.value as any })} disabled={!canEdit}><option>HUF</option><option>EUR</option></select></Field>
            <Field label="Érvényes ettől"><input type="date" value={cur.valid_from ?? ''} onChange={(e) => upd({ valid_from: e.target.value })} disabled={!canEdit} /></Field>
            <Field label="Érvényes eddig"><input type="date" value={cur.valid_to ?? ''} onChange={(e) => upd({ valid_to: e.target.value })} disabled={!canEdit} /></Field>
          </div>
          <table className="list">
            <thead><tr><th>Termék</th><th className="num">Katalógusár</th><th className="num">Árlista szerinti ár</th><th></th></tr></thead>
            <tbody>
              {(cur.items ?? []).map((i, k) => (
                <tr key={i.product_id}>
                  <td>{i.name} <span className="muted small">{i.sku}</span></td>
                  <td className="num muted">{money(i.base_price, i.product_currency)}</td>
                  <td className="num"><input inputMode="decimal" value={String(i.unit_price)} disabled={!canEdit} style={{ width: 120, textAlign: 'right' }}
                    onChange={(e) => upd({ items: cur.items!.map((x, j) => (j === k ? { ...x, unit_price: e.target.value as any } : x)) })} /></td>
                  <td>{canEdit && <button className="link" onClick={() => upd({ items: cur.items!.filter((_, j) => j !== k) })}>törlés</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {canEdit && (
            <div className="actions">
              <select value={add} onChange={(e) => setAdd(e.target.value)} aria-label="Termék hozzáadása">
                <option value="">+ termék hozzáadása…</option>
                {products.filter((p) => !(cur.items ?? []).some((i) => i.product_id === p.id)).map((p) => <option key={p.id} value={p.id}>{p.sku} – {p.name}</option>)}
              </select>
              <button className="secondary" disabled={!add} onClick={() => { const p = products.find((x) => x.id === add)!; setAdd('');
                upd({ items: [...(cur.items ?? []), { product_id: p.id, sku: p.sku, name: p.name, unit: p.unit, base_price: p.unit_price, product_currency: p.currency, unit_price: p.unit_price }] }); }}>Hozzáad</button>
              <span className="spacer" />
              <button onClick={save} disabled={a.busy || !cur.name.trim()}>Mentés</button>
              {saved && <span className="muted small">Elmentve.</span>}
            </div>
          )}
          <ErrorBox error={a.error} />
        </section>
      )}
    </div>
  );
}
